import * as React from 'react';
import { visualize, story, snap } from 'storybook-snapper';
import { FileUpload, FileUploadFileState } from '..';
import { FileUploadVisual } from './FileUploadVisual';
import { classes } from './FileUpload.visual.st.css';

visualize('FileUpload', () => {
  const defaultProps = {
    label: 'Add files',
    buttonText: 'Click me to upload',
    explanationText: 'Add up to 2 MB ',
    files: [],
  };
  story('simple', () => {
    const tests = [
      {
        title: 'default - no files',
        props: {
          ...defaultProps,
        },
      },
      {
        title: 'with errorMessage',
        props: {
          ...defaultProps,
          errorMessage: 'Field is required',
        },
      },
      {
        title: 'single file',
        props: {
          ...defaultProps,
          files: [
            {
              file: new File([], 'image.png'),
              state: 'idle' as FileUploadFileState,
            },
          ],
        },
      },
      {
        title: 'single file with error',
        props: {
          ...defaultProps,
          files: [
            {
              file: new File([], 'image.png'),
              state: 'error' as FileUploadFileState,
              errorMessage: 'Error occurred',
            },
          ],
        },
      },
      {
        title: 'multiple files',
        props: {
          ...defaultProps,
          explanationText: '2 files uploaded',
          files: [
            {
              file: new File([], 'image1.png'),
              state: 'idle' as FileUploadFileState,
            },
            {
              file: new File([], 'image2.png'),
              state: 'idle' as FileUploadFileState,
            },
          ],
        },
      },
      {
        title: 'multiple files popup',
        props: {
          ...defaultProps,
          explanationText: '2 files uploaded',
          files: [
            {
              file: new File([], 'image1.png'),
              state: 'idle' as FileUploadFileState,
            },
            {
              file: new File([], 'image2.png'),
              state: 'idle' as FileUploadFileState,
            },
          ],
        },
        setup: {
          clickPopup: true,
        },
      },
      {
        title: 'multiple files with error',
        props: {
          ...defaultProps,
          explanationText: '2 files uploaded',
          files: [
            {
              file: new File([], 'image1.png'),
              state: 'idle' as FileUploadFileState,
            },
            {
              file: new File([], 'image2.png'),
              state: 'error' as FileUploadFileState,
              errorMessage: 'Error occurred',
            },
          ],
        },
      },
      {
        title: 'multiple files with error popup',
        props: {
          ...defaultProps,
          explanationText: '2 files uploaded',
          files: [
            {
              file: new File([], 'image1.png'),
              state: 'idle' as FileUploadFileState,
            },
            {
              file: new File([], 'image2.png'),
              state: 'error' as FileUploadFileState,
              errorMessage: 'Error occurred',
            },
          ],
        },
        setup: {
          clickPopup: true,
        },
      },
      {
        title: 'multiple files with error and errorMessage',
        props: {
          ...defaultProps,
          errorMessage: 'Error message',
          explanationText: '2 files uploaded',
          files: [
            {
              file: new File([], 'image1.png'),
              state: 'idle' as FileUploadFileState,
            },
            {
              file: new File([], 'image2.png'),
              state: 'error' as FileUploadFileState,
              errorMessage: 'Error occurred',
            },
          ],
        },
      },
      {
        title: 'multiple files with error and errorMessage popup',
        props: {
          ...defaultProps,
          errorMessage: 'Something went wrong',
          explanationText: '2 files uploaded',
          files: [
            {
              file: new File([], 'image1.png'),
              state: 'idle' as FileUploadFileState,
            },
            {
              file: new File([], 'imge2.png'),
              state: 'error' as FileUploadFileState,
              errorMessage: 'Error occurred',
            },
          ],
        },
        setup: {
          clickPopup: true,
        },
      },
      {
        title: 'single file with long name',
        props: {
          ...defaultProps,
          files: [
            {
              file: new File(
                [],
                'Lorem ipsum dolor sit amet, cu meliore consequat quo, mel ut sumo etiam dignissim.png',
              ),
              state: 'idle' as FileUploadFileState,
            },
          ],
        },
      },
      {
        title: 'multiple files with long names popup',
        props: {
          ...defaultProps,
          explanationText: '2 files uploaded',
          files: [
            {
              file: new File(
                [],
                'Lorem ipsum dolor sit amet, cu meliore consequat quo, mel ut sumo etiam dignissim.png',
              ),
              state: 'idle' as FileUploadFileState,
            },
            {
              file: new File(
                [],
                'Lorem ipsum dolor sit amet, cu meliore consequat quo, mel ut sumo etiam dignissim.jpg',
              ),
              state: 'idle' as FileUploadFileState,
            },
          ],
        },
        setup: {
          clickPopup: true,
        },
      },
    ];

    tests.forEach(({ title, props, setup }) => {
      ['rtl', 'ltr'].forEach((dir) => {
        snap(
          title + ` - ${dir}`,
          <div dir={dir}>
            <FileUploadVisual clickPopup={setup?.clickPopup}>
              <FileUpload {...props} />
            </FileUploadVisual>
          </div>,
        );
      });
    });
  });

  story('styles overrides', () => {
    const idleFile = {
      file: new File([], 'image.png'),
      state: 'idle' as const,
    };
    const tests = [
      { className: 'labelFontColor' },
      { className: 'labelFont' },
      { className: 'explanationTextColor' },
      { className: 'explanationTextFont' },
      { className: 'buttonBackgroundColor' },
      { className: 'buttonBorderColorWidthRadius' },
      { className: 'buttonTextColor' },
      { className: 'filesTextFont', props: { files: [idleFile] } },
      { className: 'filesTextColor', props: { files: [idleFile] } },
      { className: 'deleteIconColor', props: { files: [idleFile] } },
      { className: 'deleteIconSize', props: { files: [idleFile] } },
      { className: 'errorColor', props: { errorMessage: 'Error message' } },
    ];

    tests.forEach(({ className, props }) => {
      snap(
        `Overrides ${className}`,
        <FileUploadVisual>
          <FileUpload
            {...defaultProps}
            {...props}
            className={classes[className]}
          />
        </FileUploadVisual>,
      );
    });
  });
  story('wst overrides', () => {
    const testsWst = [
      { className: 'wstButtonBackgroundColor' },
      { className: 'wstTextColor' },
      { className: 'wstErrorColor', props: { errorMessage: 'Error message' },},
      { className: 'wstButtonTextColor' },
      { className: 'wstExplanationTextColor', props: { explanationText: 'Paragraph 3 color' },},
      { className: 'wstTextFont' },
    ];
    testsWst.forEach(({ className, props }) => {
      snap(
        `${className}`,
        <FileUploadVisual>
          <FileUpload
            {...defaultProps}
            {...props}
            className={classes[className]}
          />
        </FileUploadVisual>,
      );
    });
  });
});
